/**
 * Shared styles for the Stock Audit reports, so all four match the existing
 * report design language (KPI cards → filters → table → CSV/Print).
 */
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { addPrintBrand } from '../../utils/printBranding';

export const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;
export const SummaryCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;
export const SummaryIcon = styled.div`
  width: 48px; height: 48px; border-radius: 6px;
  background: ${p => p.color || colors.primary.main}15;
  color: ${p => p.color || colors.primary.main};
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
`;
export const SummaryInfo = styled.div`min-width: 0;`;
export const SummaryLabel = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 4px;`;
export const SummaryValue = styled.div`
  font-size: 20px; font-weight: 600; color: ${colors.text.primary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

export const FiltersCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 20px;
  margin-bottom: 20px;
`;
export const FiltersRow = styled.div`display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end;`;
export const FilterGroup = styled.div`display: flex; flex-direction: column; gap: 6px; min-width: 160px;`;
export const Label = styled.label`
  font-size: 11px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.3px;
`;
export const Input = styled.input`
  padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: 6px;
  font-size: 13px; color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
export const Select = styled.select`
  padding: 9px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: 6px;
  font-size: 13px; color: ${colors.text.primary}; background: white; cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;
export const ButtonGroup = styled.div`display: flex; gap: 8px; margin-left: auto;`;
export const Button = styled.button`
  padding: 9px 14px; border: 1px solid ${colors.neutral[300]}; border-radius: 6px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  background: white; color: ${colors.text.primary}; transition: all .2s;
  &:hover { background: ${colors.neutral[50]}; border-color: ${colors.primary.main}; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

export const ContentCard = styled.div`
  background: white; border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden;
`;
export const TableContainer = styled.div`overflow-x: auto;`;
export const Table = styled.table`width: 100%; border-collapse: collapse;`;
export const Th = styled.th`
  padding: 12px 14px; text-align: ${p => p.align || 'left'};
  font-size: 11px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.5px;
  background: ${colors.neutral[50]}; border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;
export const Td = styled.td`
  padding: 10px 14px; text-align: ${p => p.align || 'left'};
  font-size: 13px; color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.neutral[100]};
  small { color: #999; margin-left: 6px; }
`;
export const Tr = styled.tr`&:hover { background: ${colors.neutral[50]}; }`;
export const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};
  td { padding: 14px; font-weight: 700; }
`;
export const EmptyState = styled.div`
  text-align: center; padding: 60px 20px;
  color: ${colors.text.secondary}; font-size: 14px;
`;
export const Short = styled.span`color: ${colors.error.main}; font-weight: 600;`;
export const Extra = styled.span`color: ${colors.success.main}; font-weight: 600;`;
export const Chip = styled.span`
  display: inline-block; padding: 3px 9px; border-radius: 12px;
  font-size: 11px; font-weight: 700;
  background: ${p => p.$s === 'Approved' ? '#E8F5E9' : p.$s === 'Counting' ? '#FFF3E0' : p.$s === 'PendingApproval' ? '#E3F2FD' : '#FFEBEE'};
  color: ${p => p.$s === 'Approved' ? '#2E7D32' : p.$s === 'Counting' ? '#E65100' : p.$s === 'PendingApproval' ? '#1565C0' : '#C62828'};
`;

/** Print a simple, professional report window (matches the other reports). */
export function printReport(title, meta, tableHtml) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<html><head><title>${title}</title>
    <style>
      body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
      h1{color:#800020;font-size:20px;margin-bottom:5px}
      .meta{color:#666;font-size:12px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse}
      th{background:#800020;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
      td{padding:6px 8px;border-bottom:1px solid #ddd}
      .r{text-align:right}
      .short{color:#C62828;font-weight:600}
      .extra{color:#2E7D32;font-weight:600}
      tfoot td{border-top:2px solid #800020;font-weight:700}
      @media print{body{margin:15px}}
    </style></head><body>
    <h1>${title}</h1>
    <div class="meta">${meta}</div>
    ${tableHtml}
  </body></html>`);
  addPrintBrand(w);
  w.document.close();
  w.print();
}

/** Download rows as CSV. */
export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(a.href);
}

export const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// ── Date / time ──
export const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');
export const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
};
/** "12 Jul 2026, 04:36 AM" */
export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-PK', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
/** How long the count took, e.g. "1h 20m". */
export const duration = (startIso, endIso) => {
  if (!startIso || !endIso) return '—';
  const ms = new Date(endIso) - new Date(startIso);
  if (isNaN(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

// ── Expand / collapse ──
export const ExpandBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${colors.text.secondary}; display: flex; align-items: center;
  padding: 2px; font-size: 15px;
  &:hover { color: ${colors.primary.main}; }
`;
export const ParentRow = styled.tr`
  cursor: pointer;
  background: ${p => (p.$expanded ? colors.primary.main + '08' : 'transparent')};
  &:hover { background: ${colors.neutral[50]}; }
`;
export const SubRow = styled.tr`
  background: ${colors.neutral[50]};
  td { font-size: 12px; padding: 7px 14px; border-bottom: 1px solid ${colors.neutral[100]}; }
`;
export const SubHeadRow = styled.tr`
  background: ${colors.neutral[100]};
  td {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    color: ${colors.text.secondary}; padding: 6px 14px; letter-spacing: .4px;
  }
`;
export const SearchWrap = styled.div`
  position: relative;
  svg { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #999; }
  input { padding-left: 32px; }
`;
export const MiniNote = styled.div`
  font-size: 11px; color: #999; padding: 8px 14px;
`;
